/**
 * Parser für Deep-Link-Query-Parameter am `/new`-Route:
 *   /new?title=…&date=…&items=…&location=…&deadline=…&lang=…
 * Siehe docs/api.md §3.
 */

export type DeepLinkPrefill = {
  title?: string;
  date?: string;
  location?: string;
  deadline?: string;
  items: string[];
  lang?: string;
};

export function parseDeepLink(search: string): DeepLinkPrefill {
  const params = new URLSearchParams(search);
  const itemsRaw = params.get("items");
  const items = itemsRaw
    ? itemsRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  return {
    title: params.get("title") ?? undefined,
    date: params.get("date") ?? undefined,
    location: params.get("location") ?? undefined,
    deadline: params.get("deadline") ?? undefined,
    items,
    lang: params.get("lang") ?? undefined,
  };
}

const pad = (n: number) => n.toString().padStart(2, "0");

/** ISO → `YYYY-MM-DD` (lokal, für `<input type="date">`). */
export function isoToLocalDate(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** ISO → `HH:mm` (lokal, für `<input type="time">`). Minuten werden auf das
 * nächste 30-Min-Raster abgerundet, passend zum `step="1800"` des Inputs. */
export function isoToLocalTime(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const h = date.getHours();
  const m = date.getMinutes() < 30 ? 0 : 30;
  return `${pad(h)}:${pad(m)}`;
}

/** `YYYY-MM-DD` + `HH:mm` + optionale IANA-Zone → ISO-String mit korrektem Offset.
 *
 * Ohne `timezone`: wird die Browser-TZ benutzt (wie `new Date(...)`).
 * Mit `timezone`: der konkrete UTC-Offset der Ziel-Zone fuer dieses Datum
 * wird bestimmt (DST-aware) und das Ergebnis als ISO-String
 * `YYYY-MM-DDTHH:mm:00<offset>` geliefert.
 */
export function localDateTimeToIso(
  date: string,
  time: string,
  timezone?: string,
): string {
  if (!date || !time) return "";
  if (!timezone) {
    const parsed = new Date(`${date}T${time}`);
    if (isNaN(parsed.getTime())) return "";
    return parsed.toISOString();
  }
  // Offset der Ziel-Zone fuer dieses Datum in Minuten.
  // Wir ziehen uns den Offset via Intl.DateTimeFormat mit "longOffset" oder
  // berechnen ihn ueber den Unterschied zwischen UTC- und Zone-Komponenten.
  const offsetMin = tzOffsetMinutes(date, time, timezone);
  if (Number.isNaN(offsetMin)) return "";
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  const oh = String(Math.floor(abs / 60)).padStart(2, "0");
  const om = String(abs % 60).padStart(2, "0");
  return `${date}T${time}:00${sign}${oh}:${om}`;
}

/** Offset einer IANA-Zone in Minuten fuer einen bestimmten lokalen Zeitpunkt.
 * Positive Werte bedeuten oestlich von UTC. */
function tzOffsetMinutes(date: string, time: string, timezone: string): number {
  try {
    // Wir formatieren "jetzt" mit der Ziel-Zone und holen alle Felder,
    // um einen "Pseudo-UTC"-Timestamp zu konstruieren. Der Unterschied zur
    // echten UTC-Version der Eingabe liefert den Offset.
    const asUtc = Date.UTC(
      Number(date.slice(0, 4)),
      Number(date.slice(5, 7)) - 1,
      Number(date.slice(8, 10)),
      Number(time.slice(0, 2)),
      Number(time.slice(3, 5)),
      0,
      0,
    );
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = dtf.formatToParts(new Date(asUtc));
    const byType = Object.fromEntries(
      parts.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]),
    );
    const zonedAsUtc = Date.UTC(
      Number(byType.year),
      Number(byType.month) - 1,
      Number(byType.day),
      Number(byType.hour === "24" ? "00" : byType.hour),
      Number(byType.minute),
      Number(byType.second),
      0,
    );
    // Differenz: wie weit ist die Zone von UTC entfernt?
    // zonedAsUtc > asUtc ⇔ Zone ist oestlich (positiver Offset).
    return Math.round((zonedAsUtc - asUtc) / 60000);
  } catch {
    return Number.NaN;
  }
}
